import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const VERIFICATION_STEPS = [
  {
    step: '01',
    icon: 'ri-id-card-line',
    title: '身份证实名认证',
    subtitle: '公安系统核验',
    desc: '上传身份证正反面，系统自动与公安部人口信息数据库比对，确保真实姓名与证件一致。',
    badge: '公安系统',
    color: 'from-red-50 to-rose-50',
    border: 'border-red-100',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-500',
    badgeColor: 'bg-red-100 text-red-600',
  },
  {
    step: '02',
    icon: 'ri-graduation-cap-line',
    title: '学历证书核实',
    subtitle: '学信网权威查询',
    desc: '上传毕业证及学位证书，通过中国高等教育学生信息网（学信网）逐一核实，杜绝学历造假。',
    badge: '学信网',
    color: 'from-amber-50 to-yellow-50',
    border: 'border-amber-100',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  {
    step: '03',
    icon: 'ri-video-chat-line',
    title: '视频1对1认证',
    subtitle: '人工视频核验',
    desc: '证件审核通过后，由专业工作人员发起视频通话，确认本人与证件照片为同一人，获得视频认证徽章。',
    badge: '人工核验',
    color: 'from-green-50 to-emerald-50',
    border: 'border-green-100',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    badgeColor: 'bg-green-100 text-green-700',
  },
];

export default function TruthPledge() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div
          className="text-center mb-14 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-5 py-2 mb-5">
            <i className="ri-shield-star-line text-red-500"></i>
            <span className="text-sm font-bold text-red-600">富缘佳旺真实承诺</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            您 在这里找到<br />
            <span className="text-red-600">真实的幸运缘分</span>
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
            富缘佳旺是中国唯一要求 <strong>三重认证</strong> 的婚恋平台。每一位会员的身份、学历均经过严格核实，虚假信息将永久封号。我们承诺：<strong className="text-red-600">您在这里遇见的每一个人，都是真实的。</strong>
          </p>
        </div>

        {/* 3-Step Verification Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-14">
          {VERIFICATION_STEPS.map((item, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br ${item.color} border ${item.border} rounded-2xl p-6 transition-all duration-700`}
              style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transitionDelay: `${i * 120}ms` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 flex items-center justify-center ${item.iconBg} rounded-xl`}>
                  <i className={`${item.icon} ${item.iconColor} text-xl`}></i>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${item.badgeColor}`}>{item.badge}</span>
              </div>
              <div className="text-xs font-bold text-gray-400 mb-1">STEP {item.step}</div>
              <h4 className="text-base font-bold text-gray-800 mb-1">{item.title}</h4>
              <div className="text-xs text-gray-500 font-medium mb-3">{item.subtitle}</div>
              <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Truth Pledge Banner */}
        <div
          className="relative overflow-hidden rounded-2xl transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'scale(1)' : 'scale(0.97)', transitionDelay: '500ms' }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/c888a9838170ad8376984a2cd12d1447.jpg"
            alt="Truth Pledge"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 via-red-800/85 to-red-900/80"></div>
          <div className="relative z-10 px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 mb-2 justify-center md:justify-start">
                <i className="ri-shield-check-fill text-yellow-400 text-xl"></i>
                <span className="text-yellow-400 text-sm font-bold tracking-widest">平台郑重承诺</span>
              </div>
              <h3 className="text-white text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                虚假信息，永久封号
              </h3>
              <p className="text-white/70 text-sm max-w-xl leading-relaxed">
                任何会员若提供虚假学历或身份信息，一经发现立即永久封号，并保留追究法律责任的权利。我们用最严格的标准，守护每一位真诚用户的权益。
              </p>
            </div>
            <div className="flex flex-col gap-3 flex-shrink-0">
              <div className="flex items-center gap-3 bg-white/10 rounded-xl px-5 py-3">
                <i className="ri-check-double-line text-green-400 text-lg"></i>
                <span className="text-white text-sm font-medium">身份 · 公安系统人脸比对</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-xl px-5 py-3">
                <i className="ri-check-double-line text-green-400 text-lg"></i>
                <span className="text-white text-sm font-medium">学历 · 学信网实时核查</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-xl px-5 py-3">
                <i className="ri-check-double-line text-green-400 text-lg"></i>
                <span className="text-white text-sm font-medium">视频 · 1对1人工身份确认</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className="text-center mt-10 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '600ms' }}
        >
          <p className="text-gray-500 text-sm mb-4">加入富缘佳旺，开始您真实的缘分之旅</p>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-3 rounded-full font-semibold text-sm hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line"></i> 立即免费注册
          </Link>
        </div>
      </div>
    </section>
  );
}
